// src/pages/admin/AdminPreferencesScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, SlidersHorizontal } from 'lucide-react';

const DEFAULTS = {
  workplaceEnvironment: { matchScore: 80 },
  workplaceSize:        { matchScore: 60 },
  workplaceLocation:    { matchScore: 90 },
  positionName:         { matchScore: 70 },
  adminLevel:           { matchScore: 60 },
  employerName:         { matchScore: 50 },
  employerType:         { matchScore: 70 },
  industry:             { matchScore: 70 },
  schedule:             { matchScore: 80 },
  salary: {
    fullOverlapScore: 100,
    partialOverlapHigherScore: 60,
    partialOverlapLowerScore: 40,
    noOverlapHigherScore: 0,
    noOverlapLowerScore: 0,
  },
  benefits:             { matchScore: 60 },
};

/* ── Score row (label + input) ────────────────────────────── */
const ScoreRow = ({ label, value, onChange, indent }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0', paddingLeft: indent ? 24 : 0 }}>
    <label className="form-label" style={{ marginBottom: 0, fontSize: indent ? 12 : undefined, color: indent ? '#666' : undefined }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max="100" value={value}
        onChange={e => onChange(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888' }}>/ 100</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminPreferencesScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    workplaceEnvironment: { ...DEFAULTS.workplaceEnvironment, ...d.workplaceEnvironment },
    workplaceSize:        { ...DEFAULTS.workplaceSize, ...d.workplaceSize },
    workplaceLocation:    { ...DEFAULTS.workplaceLocation, ...d.workplaceLocation },
    positionName:         { ...DEFAULTS.positionName, ...d.positionName },
    adminLevel:           { ...DEFAULTS.adminLevel, ...d.adminLevel },
    employerName:         { ...DEFAULTS.employerName, ...d.employerName },
    employerType:         { ...DEFAULTS.employerType, ...d.employerType },
    industry:             { ...DEFAULTS.industry, ...d.industry },
    schedule:             { ...DEFAULTS.schedule, ...d.schedule },
    salary:               { ...DEFAULTS.salary, ...d.salary },
    benefits:             { ...DEFAULTS.benefits, ...d.benefits },
  });

  useEffect(() => {
    matchingModelAPI.getPreferencesConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updatePreferencesConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Seeker preferences scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const update = (section) => (patch) =>
    setConfig(c => ({ ...c, [section]: { ...c[section], ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const sal = config.salary;

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Seeker Preferences Scorer</h1>
      </div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure how seeker work preferences affect candidate scoring.
        Each field scores whether the seeker's stated preferences match the position or employer profile values.
      </p>

      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SlidersHorizontal size={18} />
          <span>Match Scores</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>

          <ScoreRow label="Workplace Environment" value={config.workplaceEnvironment.matchScore}
            onChange={(v) => update('workplaceEnvironment')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Workplace Size" value={config.workplaceSize.matchScore}
            onChange={(v) => update('workplaceSize')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Workplace Location" value={config.workplaceLocation.matchScore}
            onChange={(v) => update('workplaceLocation')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Position Name" value={config.positionName.matchScore}
            onChange={(v) => update('positionName')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Admin Level" value={config.adminLevel.matchScore}
            onChange={(v) => update('adminLevel')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Employer Name" value={config.employerName.matchScore}
            onChange={(v) => update('employerName')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Employer Type" value={config.employerType.matchScore}
            onChange={(v) => update('employerType')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Industry" value={config.industry.matchScore}
            onChange={(v) => update('industry')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Schedule" value={config.schedule.matchScore}
            onChange={(v) => update('schedule')({ matchScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          {/* Salary — main label + sub-labels */}
          <div style={{ padding: '10px 0' }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Salary</label>
          </div>
          <ScoreRow label="Full Overlap" value={sal.fullOverlapScore} indent
            onChange={(v) => update('salary')({ fullOverlapScore: v })} />
          <ScoreRow label="Partial Overlap — Position Higher" value={sal.partialOverlapHigherScore} indent
            onChange={(v) => update('salary')({ partialOverlapHigherScore: v })} />
          <ScoreRow label="Partial Overlap — Position Lower" value={sal.partialOverlapLowerScore} indent
            onChange={(v) => update('salary')({ partialOverlapLowerScore: v })} />
          <ScoreRow label="No Overlap — Position Higher" value={sal.noOverlapHigherScore} indent
            onChange={(v) => update('salary')({ noOverlapHigherScore: v })} />
          <ScoreRow label="No Overlap — Position Lower" value={sal.noOverlapLowerScore} indent
            onChange={(v) => update('salary')({ noOverlapLowerScore: v })} />
          <div style={{ borderTop: '1px solid #eee' }} />

          <ScoreRow label="Benefits" value={config.benefits.matchScore}
            onChange={(v) => update('benefits')({ matchScore: v })} />

        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
